import { Question } from 'services/campaign/types';
import { Conversation } from 'services/conversations/types';
import { User } from 'services/user/types';
import { SearchableSortDirection } from 'services/api/types';
import {
  AtsQuestionInput,
  AtsQuestionnaireAnswer,
  CandidateAnalysisErrorCode,
  RequirementRecommendationCategory
} from 'generated-graphql/types';

export enum CandidateBatchStatus {
  RANKING_CANDIDATES = 'RANKING_CANDIDATES',
  FINISHED_RANKING_CANDIDATES = 'FINISHED_RANKING_CANDIDATES',
  LIVE = 'LIVE',
  PAUSED = 'PAUSED',
  PRE_PROCESSING = 'PRE_PROCESSING',
  READY_TO_PROCESS = 'READY_TO_PROCESS',
  CREATED = 'CREATED',
  ARCHIVED = 'ARCHIVED'
}

export type RubricScore = {
  score: number;
  description: string;
};

export type Rubric = {
  relevantExperience: Array<RubricScore>;
  relevantQualifications: Array<RubricScore>;
  relevantSkills: Array<RubricScore>;
  softSkills: Array<RubricScore>;
};

export type Weights = {
  relevantExperience: number;
  relevantQualifications: number;
  relevantSkills: number;
  softSkills: number;
};

export type Requirement = {
  content: string;
  isMandatory?: boolean | null;
};

type JobDescription = {
  jobTitle: string;
  employerName: string;
  location: string;
  contractType: string;
  hours: string;
  salary: string;
  closingDate: string;
  summaryOfRole: string;
  responsibilities: string;
  qualifications: string;
  benefits: string;
  diversityEqualityInclusion: string;
};

export type CandidateBatch = {
  id: string;
  title?: string | null;
  cvS3Keys?: Array<string> | null;
  csvS3Key?: string | null;
  jobDescription: string;
  candidatesCount: number;
  candidatesToProcessCount: number;
  weights?: Weights;
  status: CandidateBatchStatus;
  jobDescriptionParsed?: JobDescription | null;
  campaignId?: string | null;
  screeningQuestions?: Array<Question> | null;
  rubric?: Rubric;
  createdAt: string;
  updatedAt: string;
  requirements?: Requirement[];
  user?: User | null;
  candidateBatchGroup?: CandidateBatchGroup | null;
  candidateBatchGroupId?: string | null;
  atsJobId?: string | null;
  atsJobReference?: string | null;
  candidatesRequirementsMetCount: number;
  candidatesRequirementsNotMetCount: number;
  isWorkflowManaged?: boolean | null;
};

export type CandidateBatchGroup = {
  id: string;
  organizationId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  user?: User | null;
};

export type ModelCandidateMessageConnection = {
  items: Array<CandidateMessage>;
  nextToken?: string | null;
};

export type CandidateRequirement = {
  content: string;
  isMet: boolean;
};

export type Candidate = {
  id: string;
  totalScore: number;
  totalScoreScaled: number | null;
  analysisErrorCode?: CandidateAnalysisErrorCode | null;
  firstName: string;
  lastName: string;
  email?: string | null;
  phoneNumber?: string | null;
  candidateBatchId: string;
  createdAt: string;
  updatedAt: string;
  conversationId?: string | null;
  cvS3Key?: string | null;
  isMessagesGenerating?: boolean | null;
  messages?: ModelCandidateMessageConnection | null;
  linkedInProfileId?: string | null;
  isAllRequirementsMet?: boolean | null;
  requirements?: Array<CandidateRequirement> | null;
  status?: CandidateStatus | null;
  conversation?: Conversation | null;
  atsCandidateId?: string | null;
  atsApplicationId?: string | null;
  atsApplicationSource?: string | null;
  cvParsedText?: string | null;
  atsQuestionnaireAnswers?: AtsQuestionnaireAnswer[];
  isLocked?: boolean | null;
};

export type CandidateWithCandidateBatch = Candidate & {
  candidateBatch: CandidateBatch;
};

export enum CandidateMessageType {
  REJECTION_MESSAGE = 'REJECTION_MESSAGE',
  INTERESTED_MESSAGE = 'INTERESTED_MESSAGE'
}

export type CandidateMessage = {
  __typename: 'CandidateMessage';
  id: string;
  body: string;
  type: CandidateMessageType;
  candidateId: string;
  createdAt: string;
  updatedAt: string;
};

export type profileData = {
  __typename: 'profileData';
  profileId: string;
  data: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * @deprecated Use generated CandidateStatus enum and TODO: remove this enum
 */
export enum CandidateStatus {
  REJECTED = 'REJECTED',
  SHORT_LISTED = 'SHORT_LISTED',
  PROGRESSING = 'PROGRESSING',
  NOT_PROGRESSING = 'NOT_PROGRESSING',
  REVIEW = 'REVIEW',
  INTERVIEW_STAGE_1 = 'INTERVIEW_STAGE_1',
  INTERVIEW_STAGE_2 = 'INTERVIEW_STAGE_2',
  OFFER = 'OFFER'
}

/**
 * @deprecated use CandidateAnyStatus in modules/analyse/types/CandidateAnyStatus
 */
export const CandidateAnyStatus = 'ANY';

export type CandidateNote = {
  id: string;
  content: string;
  candidateId: string;
  organizationId: string;
  userId: string;
  user?: User | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * @deprecated use modules/analyse/types/AnalysisRequirementInputWithKey
 */
export type AnalysisRequirementInput = {
  content: string;
  isMandatory: boolean;
  weight: number;
  id?: string | null;
};

export type AnalysisRequirementUpdateInput = {
  id: string;
  isMandatory?: boolean;
  weight?: number;
  updatedContent?: string;
  isRecommendationDismissed?: boolean | null;
  recommendationCategory?: RequirementRecommendationCategory | null;
  recommendationText?: string | null;
};

export type AnalysisRequirement = {
  id: string;
  originalRequirementText: string;
  generatedRequirementText?: string | null;
  isMandatory: boolean;
  weight: number;
  organizationId: string;
  candidateBatchId: string;
  parentAnalysisRequirementId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AnalysisRequirementCreateInput = {
  content: string;
  isMandatory: boolean;
  weight: number;
};

export type UpdateAnalysisRequirementsInput = {
  requirementsToAdd?: Array<AnalysisRequirementCreateInput> | null;
  requirementsToUpdate?: Array<AnalysisRequirementUpdateInput> | null;
  requirementsToDelete?: Array<string> | null;
  candidateBatchId: string;
};

export type RequirementOutcome = {
  __typename: 'RequirementOutcome';
  id: string;
  organizationId: string;
  isMet: boolean;
  score: number;
  evidence: string;
  candidateId: string;
  candidateBatchId: string;
  analysisRequirementId: string;
  analysisRequirement?: AnalysisRequirement | null;
  createdAt: string;
  updatedAt: string;
};

export enum CorrespondenceStatus {
  CREATED = 'CREATED',
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED'
}

export enum EmailCorrespondenceType {
  JOB_RECOMMENDATION_EMAIL = 'JOB_RECOMMENDATION_EMAIL',
  CANDIDATE_REJECTION_EMAIL = 'CANDIDATE_REJECTION_EMAIL'
}

export type CandidateEmailCorrespondence = {
  __typename: 'CandidateEmailCorrespondence';
  id: string;
  organizationId: string;
  candidateId: string;
  atsCandidateId?: string | null;
  subject: string;
  body: string;
  recipientEmailAddress: string;
  senderEmailAddress?: string | null;
  status?: CorrespondenceStatus | null;
  scheduleId?: string | null;
  scheduledAt?: string | null;
  sentAt?: string | null;
  sesMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
  type?: EmailCorrespondenceType | null;
};

export type ProcessCandidateBatchesInput = {
  candidateBatchGroupId: string;
  atsJobs: Array<AtsJobInput>;
};

export type AtsJobInput = {
  atsJobId: string;
  jobDescription: string;
  atsJobPostingId?: string;
  atsJobReference?: string | null;
  atsQuestions?: Array<AtsQuestionInput> | null;
  atsJobPostingUrl?: string | null;
  // Kombo opaque id — set only when the job came from the Kombo path.
  komboJobId?: string | null;
};

export type ProcessCandidateBatchesOutput = CandidateBatchGroup & {
  candidateBatches: {
    items: Array<CandidateBatch>;
  };
};

export type SearchableCandidateBatchSortInput = {
  field?: SearchableCandidateBatchSortableFields | null;
  direction?: SearchableSortDirection | null;
};

enum SearchableCandidateBatchSortableFields {
  id = 'id',
  cvS3Keys = 'cvS3Keys',
  csvS3Key = 'csvS3Key',
  jobDescription = 'jobDescription',
  campaignId = 'campaignId',
  candidatesCount = 'candidatesCount',
  candidatesRequirementsMetCount = 'candidatesRequirementsMetCount',
  candidatesRequirementsNotMetCount = 'candidatesRequirementsNotMetCount',
  candidatesToProcessCount = 'candidatesToProcessCount',
  organizationId = 'organizationId',
  userId = 'userId',
  createdAt = 'createdAt',
  atsJobId = 'atsJobId',
  atsJobPostingId = 'atsJobPostingId',
  atsJobReference = 'atsJobReference',
  isRecalculatingScores = 'isRecalculatingScores',
  candidateBatchGroupId = 'candidateBatchGroupId',
  isInitial = 'isInitial',
  candidateBatchReanalysisReferenceId = 'candidateBatchReanalysisReferenceId',
  updatedAt = 'updatedAt'
}
export type SearchableCandidateBatchSortableFieldsType = Partial<
  Record<SearchableCandidateBatchSortableFields, string>
>;

export type SearchableCandidateSortInput = {
  field?: SearchableCandidateSortableFields | null;
  direction?: SearchableSortDirection | null;
};

enum SearchableCandidateSortableFields {
  id = 'id',
  organizationId = 'organizationId',
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
  phoneNumber = 'phoneNumber',
  cvS3Key = 'cvS3Key',
  linkedInProfileId = 'linkedInProfileId',
  totalScore = 'totalScore',
  totalScoreScaled = 'totalScoreScaled',
  status = 'status',
  candidateBatchId = 'candidateBatchId',
  conversationId = 'conversationId',
  candidateCvsParsingId = 'candidateCvsParsingId',
  createdAt = 'createdAt',
  isMessagesGenerating = 'isMessagesGenerating',
  isAllRequirementsMet = 'isAllRequirementsMet',
  atsCandidateId = 'atsCandidateId',
  atsApplicationId = 'atsApplicationId',
  atsRejectedReasonId = 'atsRejectedReasonId',
  atsInterviewStageId = 'atsInterviewStageId',
  atsJobId = 'atsJobId',
  cvParsedText = 'cvParsedText',
  skills = 'skills',
  updatedAt = 'updatedAt'
}
export type SearchableCandidateSortableFieldsType = Partial<
  Record<SearchableCandidateSortableFields, string>
>;

export type StatusSet = {
  __typename: 'StatusSet';
  id: string;
  statuses: Array<Status>;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

export type Status = {
  __typename: 'Status';
  type: StatusType;
  value: string;
  label: string;
};

export enum StatusType {
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  REVIEW = 'REVIEW',
  INTERVIEW = 'INTERVIEW'
}
